// Sweep heatmap (owned by the web:charts agent).
//
// Node × size (or node × pps) grid of loss %, coloured on fixed 0 / 5 / 20%
// breakpoints so colour reads the same across runs. Rendered as a real <table>
// with header scopes, and every cell prints its numeric loss %, so the grid is
// legible without colour and to assistive tech. Loss values are treated as
// percentages (0..100), matching the *Pct field naming.

import { useMemo } from 'react';
import type { CellResult, NodeInfo, SweepResult } from '@/engine/types';
import { EmptyNote } from './Canvas';
import { readableOn, severityColor, useChartTheme, type ChartPalette } from './palette';

export interface HeatmapProps {
  sweep: SweepResult;
  /** Column axis. */
  axis: 'size' | 'pps';
  /** Optional node metadata for row labels. */
  nodes?: NodeInfo[];
  /** Which metric to colour by (default total loss). */
  metric?: 'totalLossPct' | 'uploadLossPct' | 'downloadLossPct' | 'latePct';
}

interface Grid {
  nodeIds: string[];
  cols: number[];
  // value[nodeId][col] = mean metric, or NaN if no cell.
  values: Map<string, Map<number, number>>;
}

function colValue(cell: CellResult, axis: 'size' | 'pps'): number {
  return axis === 'size' ? cell.size : cell.pps;
}

function buildGrid(
  sweep: SweepResult,
  axis: 'size' | 'pps',
  metric: NonNullable<HeatmapProps['metric']>,
): Grid {
  const nodeIds: string[] = [...new Set(sweep.cells.map((c) => c.nodeId))];
  const cols = [...new Set(sweep.cells.map((c) => colValue(c, axis)))].sort((a, b) => a - b);
  const sums = new Map<string, Map<number, { s: number; n: number }>>();
  for (const cell of sweep.cells) {
    const col = colValue(cell, axis);
    let row = sums.get(cell.nodeId);
    if (!row) {
      row = new Map();
      sums.set(cell.nodeId, row);
    }
    const acc = row.get(col) ?? { s: 0, n: 0 };
    acc.s += cell.run.metrics[metric];
    acc.n += 1;
    row.set(col, acc);
  }
  const values = new Map<string, Map<number, number>>();
  for (const [id, row] of sums) {
    const out = new Map<number, number>();
    for (const [col, acc] of row) out.set(col, acc.n ? acc.s / acc.n : NaN);
    values.set(id, out);
  }
  return { nodeIds, cols, values };
}

export function Heatmap({ sweep, axis, nodes, metric = 'totalLossPct' }: HeatmapProps) {
  const { palette } = useChartTheme();
  const grid = useMemo(() => buildGrid(sweep, axis, metric), [sweep, axis, metric]);
  const nameOf = (id: string) => nodes?.find((n) => n.id === id)?.name ?? id;
  const colLabel = (v: number) => (axis === 'size' ? `${v}B` : `${v}pps`);

  if (grid.nodeIds.length === 0 || grid.cols.length === 0) {
    return <EmptyNote label="No sweep cells to chart yet." />;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          borderCollapse: 'separate',
          borderSpacing: 2,
          fontSize: 12,
          color: palette.text,
        }}
      >
        <caption style={{ captionSide: 'top', textAlign: 'left', color: palette.textMuted, marginBottom: 6 }}>
          Loss % by node and {axis === 'size' ? 'packet size' : 'send rate'} (0–5–20% scale)
        </caption>
        <thead>
          <tr>
            <th scope="col" style={{ textAlign: 'left', padding: '2px 6px', color: palette.textMuted }}>
              Node
            </th>
            {grid.cols.map((c) => (
              <th
                key={c}
                scope="col"
                style={{ padding: '2px 6px', color: palette.textMuted, fontVariantNumeric: 'tabular-nums' }}
              >
                {colLabel(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.nodeIds.map((id) => (
            <tr key={id}>
              <th
                scope="row"
                style={{ textAlign: 'left', padding: '2px 6px', whiteSpace: 'nowrap', color: palette.text }}
              >
                {nameOf(id)}
              </th>
              {grid.cols.map((c) => (
                <HeatCell key={c} value={grid.values.get(id)?.get(c)} palette={palette} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HeatCell({ value, palette }: { value: number | undefined; palette: ChartPalette }) {
  if (value == null || Number.isNaN(value)) {
    return (
      <td
        style={{
          minWidth: 44,
          textAlign: 'center',
          padding: '4px 6px',
          background: palette.panel,
          color: palette.textMuted,
          borderRadius: 3,
        }}
      >
        –
      </td>
    );
  }
  const bg = severityColor(palette, value);
  return (
    <td
      style={{
        minWidth: 44,
        textAlign: 'center',
        padding: '4px 6px',
        background: bg,
        color: readableOn(bg),
        borderRadius: 3,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value.toFixed(1)}
    </td>
  );
}
